/**
 * useConnectionStore — thin facade that composes domain slices into one store.
 *
 * The implementation is intentionally kept small here. Domain-specific logic
 * lives in focused modules that can be worked on independently:
 *   - sessionConnectionSlice.ts  — active connection, connected flag, capabilities
 *   - connectionProfileSlice.ts  — saved profiles, health tracking, lock/unlock
 *   - connectionStorageAdapters.ts — split-secret and Electron credential adapters
 *
 * All callers continue to import `useConnectionStore` as before; no migration
 * of existing consumers is required.
 */

import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

import {
  type SessionConnectionSlice,
  createSessionConnectionSlice,
} from "./sessionConnectionSlice";
import {
  type ConnectionProfileSlice,
  clearConnectionProfileRequestTracking,
  createConnectionProfileSlice,
} from "./connectionProfileSlice";
import {
  connectionStorage,
  CONNECTION_STORE_NAME,
  PROFILE_SESSION_PREFIX,
  ENCRYPTED_STORE_SUFFIX,
} from "./connectionStorageAdapters";

export type ConnectionState = SessionConnectionSlice &
  ConnectionProfileSlice & {
    resetConnectionState: () => void;
  };

export const useConnectionStore = create<ConnectionState>()(
  devtools(
    persist(
      (set, get, api) => ({
        ...createSessionConnectionSlice(set, get, api),
        ...createConnectionProfileSlice(set, get, api),

        resetConnectionState: () => {
          clearConnectionProfileRequestTracking();
          for (const profile of get().connectionProfiles) {
            localStorage.removeItem(
              CONNECTION_STORE_NAME + PROFILE_SESSION_PREFIX + profile.id + ENCRYPTED_STORE_SUFFIX,
            );
          }
          void useConnectionStore.persist.clearStorage();
          set({
            connection: null,
            connected: false,
            capabilities: null,
            connectionProfiles: [],
            activeProfileId: null,
            profileHealthMap: {},
          });
        },
      }),
      {
        name: CONNECTION_STORE_NAME,
        storage: connectionStorage,
        partialize: (state) => ({
          connection: state.connection,
          connectionProfiles: state.connectionProfiles,
          activeProfileId: state.activeProfileId,
        }),
      },
    ),
    { name: "ConnectionStore", enabled: import.meta.env.DEV },
  ),
);
